<script setup>
import { computed } from 'vue'
import { resolveFrontpageIcon } from '@/utils/frontpageIcons'
import { resolveFrontpageColor } from '@/utils/frontpageColors'
import FrontpageChart from '@/components/FrontpageChart.vue'
import MdiMinus from '~icons/mdi/minus'

const props = defineProps({
  element: { type: Object, required: true },
  editing: { type: Boolean, default: false },
  dragging: { type: Boolean, default: false },
  dashboard: { type: Object, default: null },
  chartLoading: { type: Boolean, default: false },
  chartError: { type: String, default: '' },
})

const emit = defineEmits(['activate', 'edit', 'delete', 'retry', 'dragstart', 'dragover', 'dragend'])

const isExternal = computed(() => /^https?:\/\//.test(props.element.link || ''))
const isMacro = computed(() => props.element.content_type === 'macro')
const isChart = computed(() => props.element.content_type === 'chart')
const isStat = computed(() => props.element.content_type === 'statistics')

const Icon = computed(() =>
  resolveFrontpageIcon(props.element.icon, { contentType: props.element.content_type }),
)
const color = computed(() =>
  resolveFrontpageColor(props.element.color, isMacro.value ? 'emerald' : 'gray'),
)
const wide = computed(() => props.element.col_span === 2 || isChart.value)
const inactive = computed(() => isMacro.value && props.element.is_active === false)

const cardTag = computed(() => {
  if (props.editing || isMacro.value || !props.element.link) return 'button'
  if (isExternal.value) return 'a'
  return 'router-link'
})

const cardBind = computed(() => {
  if (props.editing || isMacro.value || !props.element.link) return { type: 'button' }
  if (isExternal.value) {
    return { href: props.element.link, target: '_blank', rel: 'noopener noreferrer' }
  }
  return { to: props.element.link }
})

function onActivate() {
  if (props.editing) {
    emit('edit', props.element)
    return
  }
  if (isMacro.value && !inactive.value) emit('activate', props.element)
}

function onDelete(event) {
  event.preventDefault()
  event.stopPropagation()
  emit('delete', props.element)
}
</script>

<template>
  <div
    class="fp-tile"
    :class="{
      'fp-tile--wide': wide,
      'fp-tile--editing': editing,
      'fp-tile--dragging': dragging,
      'fp-tile--inactive': inactive,
      'fp-tile--macro': isMacro,
    }"
    :draggable="editing"
    @dragstart="editing && $emit('dragstart', $event)"
    @dragover.prevent="editing && $emit('dragover', $event)"
    @dragend="editing && $emit('dragend', $event)"
  >
    <button
      v-if="editing"
      type="button"
      class="fp-tile__minus"
      :aria-label="$t('common.delete')"
      @click="onDelete"
    >
      <MdiMinus class="w-3.5 h-3.5" />
    </button>

    <div v-if="isChart" class="fp-tile__chart" @click="editing && onActivate()">
      <FrontpageChart
        :title="element.title"
        :kind="element.data"
        :data="dashboard"
        :loading="chartLoading"
        :error="chartError"
        @retry="$emit('retry')"
      />
    </div>

    <component
      :is="cardTag"
      v-else
      v-bind="cardBind"
      class="fp-tile__card"
      @click="onActivate"
    >
      <span class="fp-tile__chip" :class="`fp-accent--${color}`">
        <component :is="Icon" class="fp-tile__chip-icon" />
      </span>
      <span class="fp-tile__body">
        <span class="fp-tile__title">{{ element.title }}</span>
        <span v-if="element.description && isMacro" class="fp-tile__sub">{{ element.description }}</span>
        <span v-if="isStat" class="fp-tile__value">{{ element.value ?? '—' }}</span>
      </span>
    </component>
  </div>
</template>
